import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Trophy,
  Users,
  Gift,
  Share2,
  Copy,
  Check,
  Crown,
  Star,
  TrendingUp,
  Medal,
  Sparkles,
  ChevronRight,
  ExternalLink,
  MessageCircle,
  Instagram,
  Facebook,
  Twitter,
  Mail,
  Smartphone,
  HelpCircle,
  BookOpen,
  Info,
  Zap,
  ArrowRight,
  Play
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import BetaInviteSystem from "./BetaInviteSystem";
import ReferralOnboarding from "./ReferralOnboarding";

const ReferralDashboard = ({ user, token }) => {
  const [stats, setStats] = useState({
    total_referrals: 0,
    total_shares: 0,
    badge: "Starter",
    bonus_points_earned: 0,
    recent_referrals: [],
    stylists_referred: 0,
    clients_referred: 0
  });
  const [ambassadorStatus, setAmbassadorStatus] = useState({
    promo_active: true,
    spots_remaining: 10,
    threshold: 50,
    progress_percent: 0,
    remaining_needed: 50,
    is_winner: false,
    is_ambassador: false
  });
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [activeView, setActiveView] = useState("dashboard");

  const referralCode = user?.user_id?.slice(-8) || "BETA2024";
  const baseUrl = window.location.origin;
  const inviteLink = `${baseUrl}/register?ref=${referralCode}&beta=true`;
  const stylistInviteLink = `${baseUrl}/invite-stylist?ref=${referralCode}`;

  // Check if user has seen onboarding
  useEffect(() => {
    const hasSeenOnboarding = localStorage.getItem(`referral_onboarding_${user?.user_id}`);
    if (!hasSeenOnboarding && user?.user_id) {
      setShowOnboarding(true);
    }
  }, [user?.user_id]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    if (user?.user_id) {
      localStorage.setItem(`referral_onboarding_${user.user_id}`, "true");
    }
  };

  // Tier data for instructions
  const tiers = [
    { name: "Starter", icon: "👑", referrals: 0, reward: "100 pts/referral" },
    { name: "Supporter", icon: "🌟", referrals: 5, reward: "150 pts/referral + Badge" },
    { name: "Advocate", icon: "⭐", referrals: 10, reward: "200 pts/referral + 500 bonus" },
    { name: "Champion", icon: "🥇", referrals: 25, reward: "250 pts/referral + 1mo Pro" },
    { name: "Ambassador", icon: "🏆", referrals: 50, reward: "300 pts/referral + VIP" }
  ];

  useEffect(() => {
    fetchAllData();
  }, [token]);

  const fetchAllData = async () => {
    if (!token) return;
    
    try {
      const [statsRes, sharesRes, ambassadorRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/referrals/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/referrals/my-shares`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/referrals/my-ambassador-status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/referrals/leaderboard`)
      ]);

      setStats({
        ...statsRes.data,
        total_shares: sharesRes.data.total_shares || 0
      });

      setAmbassadorStatus(ambassadorRes.data);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
    } catch (error) {
      console.error("Failed to fetch referral data:", error);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async (link, type) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success(`${type} link copied!`);
      
      // Track share
      await axios.post(`${API}/referrals/track-share`, 
        { platform: "copy_link" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      toast.error("Couldn't copy link");
    }
  };

  const getBadgeIcon = (badge) => {
    switch (badge) {
      case "Ambassador": return "🏆";
      case "Champion": return "🥇";
      case "Advocate": return "⭐";
      case "Supporter": return "🌟";
      default: return "👑";
    }
  };

  const getBadgeColor = (badge) => {
    switch (badge) {
      case "Ambassador": return "from-amber-400 to-yellow-500";
      case "Champion": return "from-yellow-400 to-amber-500";
      case "Advocate": return "from-purple-400 to-pink-500";
      case "Supporter": return "from-blue-400 to-cyan-500";
      default: return "from-stone-400 to-stone-500";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* First-time Onboarding Modal */}
      {showOnboarding && (
        <Dialog open={showOnboarding} onOpenChange={setShowOnboarding}>
          <DialogContent className="max-w-2xl bg-stone-900 border-stone-700 p-0 overflow-hidden" aria-describedby="onboarding-desc">
            <p id="onboarding-desc" className="sr-only">Learn how the referral program works</p>
            <ReferralOnboarding 
              onComplete={handleOnboardingComplete} 
              autoPlay={true}
              showCloseButton={true}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* View Tabs */}
      <div className="flex flex-wrap items-center gap-2 border-b border-stone-700 pb-4">
        <Button
          onClick={() => setActiveView("dashboard")}
          variant={activeView === "dashboard" ? "default" : "ghost"}
          className={activeView === "dashboard" 
            ? "bg-amber-500 text-black hover:bg-amber-600" 
            : "text-stone-400 hover:text-white"
          }
        >
          <TrendingUp className="w-4 h-4 mr-2" />
          My Dashboard
        </Button>
        <Button
          onClick={() => setActiveView("instructions")}
          variant={activeView === "instructions" ? "default" : "ghost"}
          className={activeView === "instructions" 
            ? "bg-amber-500 text-black hover:bg-amber-600" 
            : "text-stone-400 hover:text-white"
          }
        >
          <BookOpen className="w-4 h-4 mr-2" />
          How It Works
        </Button>
        <Button
          onClick={() => setShowOnboarding(true)}
          variant="ghost"
          className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
        >
          <Play className="w-4 h-4 mr-2" />
          Watch Tutorial
        </Button>
        <Link to="/referral-program" target="_blank" className="ml-auto">
          <Button variant="outline" size="sm" className="border-stone-600 text-stone-400 hover:text-white">
            <ExternalLink className="w-4 h-4 mr-2" />
            Full Details
          </Button>
        </Link>
      </div>

      {/* Instructions View */}
      {activeView === "instructions" && (
        <div className="space-y-8">
          {/* How It Works Steps */}
          <Card className="bg-stone-900 border-stone-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-400" />
                How the Referral Program Works
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                {/* Step 1 */}
                <div className="relative">
                  <div className="absolute -top-2 -left-2 w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-black font-bold">1</div>
                  <div className="bg-stone-800 rounded-xl p-6 pt-8">
                    <Share2 className="w-10 h-10 text-amber-400 mb-3" />
                    <h4 className="text-white font-bold mb-2">Share Your Link</h4>
                    <p className="text-stone-400 text-sm">
                      Copy your unique referral link and share it via WhatsApp, text, social media, or email.
                    </p>
                  </div>
                </div>
                {/* Step 2 */}
                <div className="relative">
                  <div className="absolute -top-2 -left-2 w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-black font-bold">2</div>
                  <div className="bg-stone-800 rounded-xl p-6 pt-8">
                    <Users className="w-10 h-10 text-green-400 mb-3" />
                    <h4 className="text-white font-bold mb-2">Friends Sign Up</h4>
                    <p className="text-stone-400 text-sm">
                      When someone clicks your link and creates an account, they&apos;re automatically tracked as your referral.
                    </p>
                  </div>
                </div>
                {/* Step 3 */}
                <div className="relative">
                  <div className="absolute -top-2 -left-2 w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-black font-bold">3</div>
                  <div className="bg-stone-800 rounded-xl p-6 pt-8">
                    <Gift className="w-10 h-10 text-purple-400 mb-3" />
                    <h4 className="text-white font-bold mb-2">Earn Rewards</h4>
                    <p className="text-stone-400 text-sm">
                      Instantly earn loyalty points, climb through tiers, and unlock exclusive perks like free Pro membership!
                    </p>
                  </div>
                </div>
              </div>

              {/* Not a Coupon Code */}
              <div className="mt-6 bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h5 className="text-white font-bold mb-1">It&apos;s NOT a Coupon Code!</h5>
                    <p className="text-stone-400 text-sm">
                      Your referral link is unique to you and automatically tracks sign-ups. No codes to remember or enter—just share the link and earn!
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tier Progression */}
          <Card className="bg-stone-900 border-stone-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-400" />
                Referral Tiers & Rewards
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {tiers.map((tier, idx) => {
                  const isCurrentTier = tier.name === stats.badge;
                  const isAchieved = stats.total_referrals >= tier.referrals;
                  
                  return (
                    <div 
                      key={tier.name}
                      className={`flex items-center gap-4 p-4 rounded-xl border ${
                        isCurrentTier 
                          ? 'bg-amber-500/20 border-amber-500/50' 
                          : isAchieved 
                            ? 'bg-green-500/10 border-green-500/30'
                            : 'bg-stone-800/50 border-stone-700'
                      }`}
                    >
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-3xl ${
                        isAchieved ? '' : 'grayscale opacity-50'
                      }`}>
                        {tier.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className={`font-bold ${isAchieved ? 'text-white' : 'text-stone-500'}`}>
                            {tier.name}
                          </h4>
                          {isCurrentTier && (
                            <Badge className="bg-amber-500 text-black text-xs">CURRENT</Badge>
                          )}
                          {isAchieved && !isCurrentTier && (
                            <Check className="w-4 h-4 text-green-400" />
                          )}
                        </div>
                        <p className={`text-sm ${isAchieved ? 'text-stone-400' : 'text-stone-600'}`}>
                          {tier.referrals === 0 ? 'Starting tier' : `${tier.referrals} referrals required`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`font-medium ${isAchieved ? 'text-amber-400' : 'text-stone-600'}`}>
                          {tier.reward}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* FAQ */}
          <Card className="bg-stone-900 border-stone-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-purple-400" />
                Frequently Asked Questions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-stone-800 rounded-xl p-4">
                <h5 className="text-white font-bold mb-2">Who can I refer?</h5>
                <p className="text-stone-400 text-sm">Anyone! You can refer clients, stylists, barbers, or salon owners. All referrals count toward your tier progress.</p>
              </div>
              <div className="bg-stone-800 rounded-xl p-4">
                <h5 className="text-white font-bold mb-2">When do I get my points?</h5>
                <p className="text-stone-400 text-sm">Points are credited instantly when someone signs up using your referral link. No waiting period!</p>
              </div>
              <div className="bg-stone-800 rounded-xl p-4">
                <h5 className="text-white font-bold mb-2">What&apos;s the Ambassador Beta Promo?</h5>
                <p className="text-stone-400 text-sm">The first 10 users to reach Ambassador status (50 referrals) win 1 month of FREE Pro membership! Check the spots remaining on your dashboard.</p>
              </div>
              <div className="bg-stone-800 rounded-xl p-4">
                <h5 className="text-white font-bold mb-2">Can I use multiple referral codes?</h5>
                <p className="text-stone-400 text-sm">No, each new user can only be referred by one person. The first referral link they use is the one that counts.</p>
              </div>
            </CardContent>
          </Card>

          {/* CTA to go back */}
          <div className="text-center">
            <Button 
              onClick={() => setActiveView("dashboard")}
              className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-bold"
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              Start Sharing Now
            </Button>
          </div>
        </div>
      )}

      {/* Dashboard View */}
      {activeView === "dashboard" && (
        <>
      {/* Header with Ambassador Promotion */}
      <div className="bg-gradient-to-r from-stone-900 via-stone-800 to-stone-900 rounded-2xl p-6 border border-amber-500/20">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          {/* Left - Stats Summary */}
          <div className="flex items-center gap-6">
            <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${getBadgeColor(stats.badge)} flex items-center justify-center text-4xl shadow-lg`}>
              {getBadgeIcon(stats.badge)}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white mb-1">Referral Dashboard</h2>
              <div className="flex items-center gap-2">
                <Badge className={`bg-gradient-to-r ${getBadgeColor(stats.badge)} text-black font-bold`}>
                  {stats.badge}
                </Badge>
                {ambassadorStatus.is_winner && (
                  <Badge className="bg-green-500 text-white">
                    <Trophy className="w-3 h-3 mr-1" />
                    Winner!
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Right - Quick Action */}
          <Button 
            onClick={() => setShowInviteModal(true)}
            className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-bold px-6"
            data-testid="invite-now-btn"
          >
            <Share2 className="w-5 h-5 mr-2" />
            Invite Friends
          </Button>
        </div>

        {/* Ambassador Progress */}
        {ambassadorStatus.promo_active && !ambassadorStatus.is_winner && (
          <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-400" />
                <span className="font-bold text-amber-400">Share to Earn Challenge</span>
              </div>
              <Badge className="bg-red-500 text-white animate-pulse">
                {ambassadorStatus.spots_remaining} spots left!
              </Badge>
            </div>
            <p className="text-stone-400 text-sm mb-3">
              First 10 users to reach 50 referrals win 1 month FREE Pro membership!
            </p>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-stone-400">Your Progress</span>
                <span className="text-amber-400 font-bold">
                  {stats.total_referrals} / {ambassadorStatus.threshold}
                </span>
              </div>
              <Progress value={ambassadorStatus.progress_percent} className="h-3 bg-stone-700" />
              <p className="text-xs text-center text-stone-500">
                {ambassadorStatus.remaining_needed > 0 
                  ? `${ambassadorStatus.remaining_needed} more referrals to become an Ambassador!`
                  : "🎉 You've reached Ambassador status!"
                }
              </p>
            </div>
          </div>
        )}

        {/* Winner Badge */}
        {ambassadorStatus.is_winner && (
          <div className="mt-6 p-4 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-xl text-black text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Trophy className="w-6 h-6" />
              <span className="font-bold text-lg">AMBASSADOR WINNER</span>
              <Trophy className="w-6 h-6" />
            </div>
            <p className="text-black/80">You&apos;ve earned 1 month FREE Pro membership!</p>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-stone-900 border-stone-700">
          <CardContent className="pt-6 text-center">
            <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <Share2 className="w-6 h-6 text-amber-400" />
            </div>
            <p className="text-3xl font-bold text-white">{stats.total_shares}</p>
            <p className="text-stone-400 text-sm">Invites Sent</p>
          </CardContent>
        </Card>

        <Card className="bg-stone-900 border-stone-700">
          <CardContent className="pt-6 text-center">
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <Users className="w-6 h-6 text-green-400" />
            </div>
            <p className="text-3xl font-bold text-white">{stats.total_referrals}</p>
            <p className="text-stone-400 text-sm">People Joined</p>
          </CardContent>
        </Card>

        <Card className="bg-stone-900 border-stone-700">
          <CardContent className="pt-6 text-center">
            <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <Gift className="w-6 h-6 text-purple-400" />
            </div>
            <p className="text-3xl font-bold text-white">{stats.bonus_points_earned}</p>
            <p className="text-stone-400 text-sm">Bonus Points</p>
          </CardContent>
        </Card>

        <Card className="bg-stone-900 border-stone-700">
          <CardContent className="pt-6 text-center">
            <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <TrendingUp className="w-6 h-6 text-blue-400" />
            </div>
            <p className="text-3xl font-bold text-white">
              {stats.total_referrals > 0 
                ? Math.round((stats.total_referrals / Math.max(stats.total_shares, 1)) * 100) 
                : 0}%
            </p>
            <p className="text-stone-400 text-sm">Conversion Rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Links & Sharing */}
        <div className="lg:col-span-2 space-y-6">
          {/* Your Referral Links */}
          <Card className="bg-stone-900 border-stone-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Share2 className="w-5 h-5 text-amber-400" />
                Your Referral Links
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* General Invite Link */}
              <div className="space-y-2">
                <label className="text-sm text-stone-400">General Invite Link</label>
                <div className="flex gap-2">
                  <Input
                    value={inviteLink}
                    readOnly
                    className="bg-stone-800 border-stone-700 text-white text-sm"
                  />
                  <Button
                    onClick={() => copyLink(inviteLink, "Invite")}
                    className={copied ? "bg-green-600" : "bg-amber-500 hover:bg-amber-600 text-black"}
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Stylist-Specific Link */}
              <div className="space-y-2">
                <label className="text-sm text-stone-400">Stylist Recruitment Page</label>
                <div className="flex gap-2">
                  <Input
                    value={stylistInviteLink}
                    readOnly
                    className="bg-stone-800 border-stone-700 text-white text-sm"
                  />
                  <Button
                    onClick={() => copyLink(stylistInviteLink, "Stylist invite")}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <a 
                    href={stylistInviteLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline" className="border-stone-600">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </a>
                </div>
              </div>

              {/* Quick Share Buttons */}
              <div className="pt-4 border-t border-stone-700">
                <label className="text-sm text-stone-400 block mb-3">Quick Share</label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(inviteLink)}`, '_blank')}
                    className="bg-green-600 hover:bg-green-700"
                    size="sm"
                  >
                    <MessageCircle className="w-4 h-4 mr-1" />
                    WhatsApp
                  </Button>
                  <Button
                    onClick={() => window.open(`sms:?body=${encodeURIComponent(inviteLink)}`, '_blank')}
                    className="bg-blue-600 hover:bg-blue-700"
                    size="sm"
                  >
                    <Smartphone className="w-4 h-4 mr-1" />
                    Text
                  </Button>
                  <Button
                    onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(inviteLink)}`, '_blank')}
                    className="bg-sky-500 hover:bg-sky-600"
                    size="sm"
                  >
                    <Twitter className="w-4 h-4 mr-1" />
                    Twitter
                  </Button>
                  <Button
                    onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(inviteLink)}`, '_blank')}
                    className="bg-blue-700 hover:bg-blue-800"
                    size="sm"
                  >
                    <Facebook className="w-4 h-4 mr-1" />
                    Facebook
                  </Button>
                  <Button
                    onClick={() => window.open(`mailto:?subject=Join MyHairChair!&body=${encodeURIComponent(inviteLink)}`, '_blank')}
                    className="bg-stone-600 hover:bg-stone-700"
                    size="sm"
                  >
                    <Mail className="w-4 h-4 mr-1" />
                    Email
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Referrals */}
          <Card className="bg-stone-900 border-stone-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-green-400" />
                Recent Sign-ups from Your Invites
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats.recent_referrals?.length > 0 ? (
                <div className="space-y-3">
                  {stats.recent_referrals.map((ref, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-3 bg-stone-800/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-full flex items-center justify-center text-black font-bold">
                          {ref.referred_name?.charAt(0) || "?"}
                        </div>
                        <div>
                          <p className="text-white font-medium">{ref.referred_name}</p>
                          <p className="text-stone-500 text-xs">
                            {new Date(ref.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <Badge className={
                        ref.referred_role === "client" 
                          ? "bg-blue-500/20 text-blue-400" 
                          : "bg-amber-500/20 text-amber-400"
                      }>
                        {ref.referred_role}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-stone-600 mx-auto mb-3" />
                  <p className="text-stone-400">No referrals yet</p>
                  <p className="text-stone-500 text-sm">Share your link to get started!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Leaderboard & Milestones */}
        <div className="space-y-6">
          {/* Leaderboard */}
          <Card className="bg-stone-900 border-stone-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-400" />
                Top Referrers
              </CardTitle>
            </CardHeader>
            <CardContent>
              {leaderboard.length > 0 ? (
                <div className="space-y-2">
                  {leaderboard.map((entry, idx) => (
                    <div 
                      key={idx}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        idx === 0 ? "bg-amber-500/20 border border-amber-500/30" :
                        idx === 1 ? "bg-stone-700/50" :
                        idx === 2 ? "bg-amber-900/30" :
                        "bg-stone-800/30"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          idx === 0 ? "bg-amber-500 text-black" :
                          idx === 1 ? "bg-stone-400 text-black" :
                          idx === 2 ? "bg-amber-700 text-white" :
                          "bg-stone-600 text-white"
                        }`}>
                          {idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : entry.rank}
                        </div>
                        <span className="text-white font-medium">{entry.name}</span>
                      </div>
                      <span className="text-amber-400 font-bold">{entry.referrals}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Trophy className="w-10 h-10 text-stone-600 mx-auto mb-2" />
                  <p className="text-stone-400 text-sm">Be the first on the leaderboard!</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Milestone Progress */}
          <Card className="bg-stone-900 border-stone-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Medal className="w-5 h-5 text-purple-400" />
                Referral Milestones
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { name: "Supporter", threshold: 5, icon: "🌟" },
                  { name: "Advocate", threshold: 10, icon: "⭐" },
                  { name: "Champion", threshold: 25, icon: "🥇" },
                  { name: "Ambassador", threshold: 50, icon: "🏆" }
                ].map((milestone, idx) => {
                  const achieved = stats.total_referrals >= milestone.threshold;
                  const progress = Math.min(100, (stats.total_referrals / milestone.threshold) * 100);
                  
                  return (
                    <div key={idx} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={achieved ? "" : "grayscale opacity-50"}>
                            {milestone.icon}
                          </span>
                          <span className={achieved ? "text-white font-medium" : "text-stone-500"}>
                            {milestone.name}
                          </span>
                        </div>
                        <span className={achieved ? "text-green-400" : "text-stone-500"}>
                          {achieved ? "✓" : `${milestone.threshold} referrals`}
                        </span>
                      </div>
                      <Progress 
                        value={progress} 
                        className={`h-1.5 ${achieved ? "bg-green-900" : "bg-stone-700"}`}
                      />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Referral Breakdown */}
          <Card className="bg-stone-900 border-stone-700">
            <CardHeader>
              <CardTitle className="text-white text-sm">Referral Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-stone-400">Stylists/Barbers</span>
                  <span className="text-amber-400 font-bold">{stats.stylists_referred || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-stone-400">Clients</span>
                  <span className="text-blue-400 font-bold">{stats.clients_referred || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
        </>
      )}

      {/* Invite Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="max-w-lg bg-stone-900 border-stone-700 text-white max-h-[90vh] overflow-y-auto" aria-describedby="invite-dialog-desc">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Invite Friends to MyHairChair</DialogTitle>
          </DialogHeader>
          <p id="invite-dialog-desc" className="sr-only">Share MyHairChair with friends and earn rewards</p>
          <BetaInviteSystem user={user} onClose={() => setShowInviteModal(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReferralDashboard;
